import { useMemo } from 'react';
import { useGetList } from 'react-admin';
import type { Category, Merchant } from '../dataProvider';
import type { CategoryResolver, StoreResolver } from './pageSchemaModel';

/**
 * The reference-resolution catalog (D-page-schema-editor-8a). Block store / category
 * references are SLUGS the author picks from the EXISTING catalog (no new data):
 * stores resolve from `merchants` (slug -> brand name), categories from `categories`
 * (slug -> label). The pickers search this catalog; resolution turns a stored slug
 * back into the real name so the structural view never shows a dangling id.
 *
 * Boost metadata on store cards is DEFERRED to the live preview (8c); this resolves
 * NAMES only, it does not run the rate-rules resolver.
 */

export interface CatalogOption {
  /** The stable slug stored on the block. */
  slug: string;
  /** The human name shown in the picker and the structural view. */
  name: string;
}

export interface Catalog {
  stores: CatalogOption[];
  categories: CatalogOption[];
  resolveStore: StoreResolver;
  resolveCategory: CategoryResolver;
  isLoading: boolean;
}

export const useCatalog = (): Catalog => {
  const { data: merchants = [], isLoading: storesLoading } =
    useGetList<Merchant>('merchants', {
      pagination: { page: 1, perPage: 500 },
      sort: { field: 'name', order: 'ASC' },
    });
  const { data: categories = [], isLoading: catsLoading } =
    useGetList<Category>('categories', {
      pagination: { page: 1, perPage: 500 },
      sort: { field: 'name', order: 'ASC' },
    });

  return useMemo(() => {
    // Stores are referenced by BRAND slug (the BLOCKS.md convention), so a unique
    // slug -> name map over merchants (one entry per brand, de-duplicated).
    const storeBySlug = new Map<string, string>();
    for (const m of merchants) {
      if (!storeBySlug.has(m.slug)) storeBySlug.set(m.slug, m.name);
    }
    const catBySlug = new Map<string, string>();
    for (const c of categories) {
      if (!catBySlug.has(c.slug)) catBySlug.set(c.slug, c.name);
    }
    const stores: CatalogOption[] = [...storeBySlug].map(([slug, name]) => ({
      slug,
      name,
    }));
    const cats: CatalogOption[] = [...catBySlug].map(([slug, name]) => ({
      slug,
      name,
    }));
    return {
      stores,
      categories: cats,
      resolveStore: (slug: string) => storeBySlug.get(slug) ?? slug,
      resolveCategory: (slug: string) => catBySlug.get(slug) ?? slug,
      isLoading: storesLoading || catsLoading,
    };
  }, [merchants, categories, storesLoading, catsLoading]);
};
